import classNames from 'classnames'
import _ from 'lodash'
import React from 'react'
import './_time-wheel-picker.scss'

export type Period = 'AM' | 'PM'

export interface ITimeWheelPickerProps {
  hour: number | null
  minute: number | null
  period: Period | null
  minuteStep: number
  onChange: (hour: number, minute: number, period: Period) => void
  isDisabled?: boolean
}

interface IWheelColumnOption {
  label: string
  value: string | number
}

interface IWheelColumnProps {
  options: IWheelColumnOption[]
  selectedValue: string | number | null
  onChange: (value: string | number) => void
  isDisabled?: boolean
}

interface IWheelColumnState {
  scrolling: boolean
}

const ITEM_HEIGHT = 36
const VISIBLE_ITEMS = 5
const PADDING_ITEMS = Math.floor(VISIBLE_ITEMS / 2)

class WheelColumn extends React.Component<IWheelColumnProps, IWheelColumnState> {
  private scrollRef = React.createRef<HTMLDivElement>()
  private scrollTimeout: ReturnType<typeof setTimeout> | null = null
  private programmaticScroll = false

  constructor(props: IWheelColumnProps) {
    super(props)
    this.state = { scrolling: false }
  }

  public componentDidMount() {
    this.scrollToSelected(false)
  }

  public componentDidUpdate(prevProps: IWheelColumnProps) {
    if (prevProps.selectedValue !== this.props.selectedValue && !this.state.scrolling) {
      this.scrollToSelected(true)
    }
  }

  public componentWillUnmount() {
    if (this.scrollTimeout) clearTimeout(this.scrollTimeout)
  }

  private getSelectedIndex(): number {
    const { options, selectedValue } = this.props
    return options.findIndex((o) => o.value === selectedValue)
  }

  private scrollToSelected(animate: boolean) {
    const index = this.getSelectedIndex()
    if (index < 0 || !this.scrollRef.current) return
    const top = index * ITEM_HEIGHT
    this.programmaticScroll = true
    if (animate) {
      this.scrollRef.current.scrollTo({ top, behavior: 'smooth' })
      setTimeout(() => { this.programmaticScroll = false }, 350)
    } else {
      this.scrollRef.current.scrollTop = top
      this.programmaticScroll = false
    }
  }

  private handleScroll = () => {
    if (this.programmaticScroll || !this.scrollRef.current) return
    if (this.scrollTimeout) clearTimeout(this.scrollTimeout)
    this.setState({ scrolling: true })
    this.scrollTimeout = setTimeout(() => {
      if (!this.scrollRef.current) return
      const index = Math.round(this.scrollRef.current.scrollTop / ITEM_HEIGHT)
      const clampedIndex = Math.max(0, Math.min(index, this.props.options.length - 1))
      const option = this.props.options[clampedIndex]
      if (option && option.value !== this.props.selectedValue) {
        this.props.onChange(option.value)
      }
      this.setState({ scrolling: false })
    }, 150)
  }

  private handleItemClick = (value: string | number) => {
    if (this.props.isDisabled) return
    this.props.onChange(value)
  }

  public render() {
    const { options, selectedValue, isDisabled } = this.props

    return (
      <div
        className={classNames('c-timeWheel-column', { 'c-timeWheel-column--disabled': isDisabled })}
        ref={this.scrollRef}
        onScroll={this.handleScroll}
      >
        {_.range(PADDING_ITEMS).map((i) => (
          <div key={`pad-top-${i}`} className='c-timeWheel-item c-timeWheel-item--pad' />
        ))}
        {options.map((option) => (
          <div
            key={String(option.value)}
            className={classNames('c-timeWheel-item', {
              'c-timeWheel-item--selected': option.value === selectedValue,
            })}
            onClick={() => this.handleItemClick(option.value)}
          >
            {option.label}
          </div>
        ))}
        {_.range(PADDING_ITEMS).map((i) => (
          <div key={`pad-bot-${i}`} className='c-timeWheel-item c-timeWheel-item--pad' />
        ))}
      </div>
    )
  }
}

export class TimeWheelPicker extends React.Component<ITimeWheelPickerProps> {
  public render() {
    const { hour, minute, period, minuteStep, onChange, isDisabled } = this.props

    const hourOptions: IWheelColumnOption[] = _.range(1, 13).map((h) => ({
      label: String(h),
      value: h,
    }))

    const minuteOptions: IWheelColumnOption[] = _.range(0, 60, minuteStep).map((m) => ({
      label: m < 10 ? `0${m}` : String(m),
      value: m,
    }))

    const periodOptions: IWheelColumnOption[] = [
      { label: 'AM', value: 'AM' },
      { label: 'PM', value: 'PM' },
    ]

    const handleHourChange = (v: string | number) =>
      onChange(v as number, minute ?? 0, period ?? 'AM')
    const handleMinuteChange = (v: string | number) =>
      onChange(hour ?? 12, v as number, period ?? 'AM')
    const handlePeriodChange = (v: string | number) =>
      onChange(hour ?? 12, minute ?? 0, v as Period)

    return (
      <div className='c-timeWheel'>
        <div className='c-timeWheel-track' />
        <WheelColumn
          options={hourOptions}
          selectedValue={hour}
          onChange={handleHourChange}
          isDisabled={isDisabled}
        />
        <div className='c-timeWheel-separator'>:</div>
        <WheelColumn
          options={minuteOptions}
          selectedValue={minute}
          onChange={handleMinuteChange}
          isDisabled={isDisabled}
        />
        <WheelColumn
          options={periodOptions}
          selectedValue={period}
          onChange={handlePeriodChange}
          isDisabled={isDisabled}
        />
      </div>
    )
  }
}
